import * as Popover from '@radix-ui/react-popover';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import {
  ANNOTATION_TEXT_COLOR_VALUES,
  DEFAULT_ANNOTATION_TEXT_COLOR,
  getAnnotationDisplayLabel,
  getAnnotationKind,
  type Annotation,
  type AnnotationRect,
  type AnnotationTextColor,
} from './annotationTypes';
import { NoteEditor } from './NoteEditor';
import styles from './AnnotationPopover.module.css';

interface AnnotationPopoverProps {
  anchorRect?: AnnotationRect | null;
  annotation: Annotation;
  children?: ReactNode;
  noteTextColor?: AnnotationTextColor;
  onDelete: (annotation: Annotation) => Promise<void> | void;
  onOpenChange: (nextOpen: boolean) => void;
  onSave: (annotation: Annotation) => Promise<void> | void;
  open: boolean;
}

export function AnnotationPopover({
  anchorRect = null,
  annotation,
  children,
  noteTextColor = DEFAULT_ANNOTATION_TEXT_COLOR,
  onDelete,
  onOpenChange,
  onSave,
  open,
}: AnnotationPopoverProps) {
  const [isEditing, setIsEditing] = useState(false);
  const resolvedTextColor = annotation.textColor ?? noteTextColor;
  const usesDarkNoteBackground = resolvedTextColor === 'white';

  useEffect(() => {
    if (!open || typeof window === 'undefined') {
      return;
    }

    const startX = window.scrollX;
    const startY = window.scrollY;
    const scrollListenerOptions = {
      capture: true,
      passive: true,
    } satisfies AddEventListenerOptions;

    const handleScroll = () => {
      const distance = Math.max(
        Math.abs(window.scrollX - startX),
        Math.abs(window.scrollY - startY),
      );

      if (distance > 24) {
        onOpenChange(false);
      }
    };

    window.addEventListener('scroll', handleScroll, scrollListenerOptions);

    return () => {
      window.removeEventListener('scroll', handleScroll, scrollListenerOptions);
    };
  }, [onOpenChange, open]);

  return (
    <Popover.Root
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setIsEditing(false);
        }
        onOpenChange(nextOpen);
      }}
      open={open}
    >
      {children ? (
        <Popover.Trigger asChild>{children}</Popover.Trigger>
      ) : anchorRect ? (
        <Popover.Anchor asChild>
          <div
            aria-hidden="true"
            className={styles.anchor}
            style={{
              height: anchorRect.height,
              left: anchorRect.left,
              top: anchorRect.top,
              width: anchorRect.width,
            }}
          />
        </Popover.Anchor>
      ) : null}
      <Popover.Portal>
        <Popover.Content
          align="start"
          className={styles.content}
          data-note-theme={usesDarkNoteBackground ? 'dark' : 'light'}
          onOpenAutoFocus={(event) => event.preventDefault()}
          side="right"
          sideOffset={8}
          style={
            {
              '--annotation-note-text-color':
                ANNOTATION_TEXT_COLOR_VALUES[resolvedTextColor],
            } as CSSProperties
          }
        >
          {isEditing ? (
            <NoteEditor
              initialValue={annotation.note}
              onCancel={() => setIsEditing(false)}
              onSave={async (note) => {
                await onSave({
                  ...annotation,
                  kind: getAnnotationKind(note),
                  note,
                });
                setIsEditing(false);
              }}
              saveLabel="Update note"
            />
          ) : (
            <div className={styles.body}>
              <div className={styles.header}>
                <p className={styles.eyebrow}>{getAnnotationDisplayLabel(annotation)}</p>
                <p className={styles.timestamp}>
                  {new Intl.DateTimeFormat(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  }).format(annotation.timestamp)}
                </p>
              </div>

              <blockquote className={styles.quote}>{annotation.quote.exact}</blockquote>

              {annotation.note ? (
                <p className={styles.note}>{annotation.note}</p>
              ) : (
                <p className={styles.notePlaceholder}>
                  This highlight does not have a saved note yet.
                </p>
              )}

              <div className={styles.actions}>
                <Button
                  onClick={() => setIsEditing(true)}
                  size="small"
                  variant="secondary"
                >
                  {annotation.note ? 'Edit note' : 'Add note'}
                </Button>
                <Button
                  destructive
                  onClick={() => {
                    void onDelete(annotation);
                  }}
                  size="small"
                  variant="secondary"
                >
                  Delete
                </Button>
              </div>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
